import type { Timestamp } from 'firebase/firestore'

export type WorkPace = 'RELAXED' | 'BALANCED' | 'AGGRESSIVE'
export type DetailLevel = 'DENSE' | 'SIMPLE'
export type OutcomeType = 'PROJECT_COMPLETED' | 'TASK_BLOCKED' | 'USER_FEEDBACK' | 'SYSTEM_FLAG'
export type ConstraintOperator = 'eq' | 'neq' | 'gt' | 'lt' | 'contains'
export type ConstraintSeverity = 'HARD' | 'SOFT'

/** Scope a saved user rule applies to. */
export type UserRuleScope = 'CREATE' | 'EDIT' | 'ALL'
/** How the rule entered the store — manual capture vs auto-detected from a chat turn. */
export type UserRuleSource = 'MANUAL' | 'DETECTED'

export interface UserRule {
  id: string
  text: string
  scope: UserRuleScope
  source: UserRuleSource
  createdAt: Timestamp
}

export type UserRuleInput = Omit<UserRule, 'id' | 'createdAt'>

export interface WorkStyle {
  pace: WorkPace
  detailLevel: DetailLevel
  planningHorizonDays: number
}

export interface Outcome {
  id: string
  timestamp: Timestamp
  summary: string
  outcomeType: OutcomeType
}

export type OutcomeInput = Omit<Outcome, 'id' | 'timestamp'>

export interface Constraint {
  key: string
  operator: ConstraintOperator
  value: unknown
  severity: ConstraintSeverity
}

export interface CognitiveProfile {
  uid: string
  preferences: Record<string, unknown>
  workStyle: WorkStyle
  constraints: Constraint[]
  userRules: UserRule[]
  projectHistory: Outcome[]
  lastUpdatedAt: Timestamp | null
}

export interface CognitiveProfilePatch {
  preferences?: Record<string, unknown>
  workStyle?: Partial<WorkStyle>
  constraints?: Constraint[]
  userRules?: UserRule[]
  projectHistory?: OutcomeInput[]
}

export type MemoryUpdate = OutcomeInput | CognitiveProfilePatch

export const createEmptyCognitiveProfile = (uid: string): CognitiveProfile => ({
  uid,
  preferences: {},
  workStyle: {
    pace: 'BALANCED',
    detailLevel: 'SIMPLE',
    planningHorizonDays: 30,
  },
  constraints: [],
  userRules: [],
  projectHistory: [],
  lastUpdatedAt: null,
})
